import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { inviteColleague } from "../lib/api";
import { track } from "../lib/analytics";
import { isValidEmail, titleCaseName } from "../lib/dataHygiene";

/**
 * Phase 1.5c — "Loop in a colleague" modal.
 *
 * Opened from the portal proposal right rail. Collects name + email + an
 * optional short note, POSTs to /api/portal/proposal/{id}/invite-colleague,
 * and reports back remaining slots.
 *
 * Props:
 *   open      — boolean
 *   onClose   — fn()
 *   proposalId — string
 *   slotsUsed  — number (colleagues already invited on this proposal)
 *   maxAllowed — number (server-enforced cap, 3)
 *   onInvited  — fn(newCount) after a successful invite
 *   role       — "primary" | "colleague"  (colleague role should never see
 *                this, but we defensively block submission)
 */
export default function InviteColleagueModal({
    open,
    onClose,
    proposalId,
    slotsUsed = 0,
    maxAllowed = 3,
    onInvited,
    role = "primary",
}) {
    const { t } = useTranslation();
    const nameRef = useRef(null);

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [note, setNote] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState(null);
    const [sent, setSent] = useState(false);

    useEffect(() => {
        if (!open) return;
        setName(""); setEmail(""); setNote("");
        setErr(null); setSent(false);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        setTimeout(() => nameRef.current?.focus(), 30);
        const onKey = (e) => e.key === "Escape" && onClose?.();
        document.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = prev;
            document.removeEventListener("keydown", onKey);
        };
    }, [open, onClose]);

    if (!open) return null;

    const canSubmit =
        role === "primary" &&
        !submitting &&
        name.trim().length > 0 &&
        isValidEmail(email.trim()) &&
        slotsUsed < maxAllowed;

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSubmitting(true);
        setErr(null);
        try {
            track("colleague_invite_submit", { proposalId });
            const res = await inviteColleague(
                proposalId, name.trim(), email.trim(), note.trim() || undefined,
            );
            setSent(true);
            onInvited?.(res.colleagues_count || (slotsUsed + 1));
        } catch (e2) {
            const detail = e2?.response?.data?.detail;
            setErr(
                typeof detail === "object" ? detail?.message : (detail || t("colleague.err.generic")),
            );
            track("colleague_invite_fail", { proposalId });
        } finally {
            setSubmitting(false);
        }
    };

    const remaining = Math.max(0, maxAllowed - slotsUsed);

    return (
        <div
            className="reveal-overlay active"
            data-testid="colleague-modal-overlay"
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
            <div
                className="reveal-modal colleague-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="colleague-modal-title"
                data-testid="colleague-modal"
            >
                <button
                    className="reveal-close"
                    aria-label={t("colleague.closeAria")}
                    onClick={onClose}
                    data-testid="colleague-modal-close"
                />
                <div className="reveal-open reveal-section">
                    <div className="reveal-section-label">
                        {t("colleague.label")}
                    </div>
                    <h2 id="colleague-modal-title">
                        {t("colleague.h2a")} <em>{t("colleague.h2b")}</em>
                    </h2>
                    <p>{t("colleague.body")}</p>
                </div>

                <form className="colleague-form reveal-section" onSubmit={submit}>
                    {sent ? (
                        <div
                            className="colleague-sent"
                            role="status"
                            data-testid="colleague-modal-sent"
                        >
                            <div className="reveal-section-label">
                                {t("colleague.sentLabel")}
                            </div>
                            <p>{t("colleague.sentBody", { email })}</p>
                        </div>
                    ) : (
                        <>
                            <label className="colleague-field">
                                <span className="mono">{t("colleague.nameLabel")}</span>
                                <input
                                    ref={nameRef}
                                    type="text"
                                    className="wiz-input"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    onBlur={(e) => setName(titleCaseName(e.target.value))}
                                    required
                                    data-testid="colleague-name-input"
                                    maxLength={120}
                                />
                            </label>
                            <label className="colleague-field">
                                <span className="mono">{t("colleague.emailLabel")}</span>
                                <input
                                    type="email"
                                    className="wiz-input"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    onBlur={(e) => setEmail(e.target.value.trim())}
                                    required
                                    data-testid="colleague-email-input"
                                    maxLength={200}
                                />
                            </label>
                            <label className="colleague-field">
                                <span className="mono">{t("colleague.noteLabel")}</span>
                                <textarea
                                    className="wiz-input"
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    rows={3}
                                    maxLength={300}
                                    placeholder={t("colleague.notePlaceholder")}
                                    data-testid="colleague-note-input"
                                />
                                <small className="muted">
                                    {t("colleague.noteCounter", {
                                        used: note.length,
                                        max: 300,
                                    })}
                                </small>
                            </label>
                            {err && (
                                <div className="wiz-warn" data-testid="colleague-err">{err}</div>
                            )}
                            <div className="colleague-meta mono">
                                {t("colleague.slotsRemaining", { remaining, max: maxAllowed })}
                            </div>
                        </>
                    )}
                </form>

                <div className="reveal-cta-row">
                    {sent ? (
                        <button
                            type="button"
                            className="reveal-primary"
                            onClick={onClose}
                            data-testid="colleague-modal-done"
                        >
                            {t("colleague.doneCta")}
                        </button>
                    ) : (
                        <>
                            <button
                                type="button"
                                className="reveal-primary"
                                onClick={submit}
                                disabled={!canSubmit}
                                data-testid="colleague-modal-submit"
                            >
                                {submitting ? t("colleague.submittingCta") : t("colleague.submitCta")}
                            </button>
                            <button
                                type="button"
                                className="reveal-dismiss"
                                onClick={onClose}
                                data-testid="colleague-modal-cancel"
                            >
                                {t("colleague.cancelCta")}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
